// @flow strict
import get from 'lodash/get';

import config from 'Config';
import { BUILD_NUMBER } from 'Lib/appVersion';
import { type GlobalStateType } from 'Lib/reduxType';
import { calculateAge } from 'Lib/utils/dateTime';
import getDeviceId from 'Lib/utils/deviceId';
import { milliseconds as perfTimer } from 'Lib/utils/perfTimer';

import { getLayout } from 'Store/appSettings/selectors';
import { getQueryParams } from 'Store/context/selectors';
import { getCurrentLanguage } from 'Store/internationalization/selectors';
import { getLatitude, getLongitude } from 'Store/location/selectors';
import { getOnboardingUserId } from 'Store/mfa/selectors';
import {
  getBirthday,
  getEmail,
  getGender,
  getGenderFilter,
  getProfileUserId,
} from 'Store/profile/selectors';
import type { GenderFilter } from 'Store/profile/user.types';

import {
  getAppSessionAdjustedTime,
  getAppSessionId,
  getAppSessionTimeElapsed,
  getUserSessionId,
  getUserSessionTimeElapsed,
} from '../../session/selectors';

const deviceId = getDeviceId();

export const isTinderEmail = /@((go)?tinder\.com|ext\.gotinder\.com|fortegrp\.com)$/i;

type MinimalDataType = {|
  appBuild: number,
  appSessionId: ?string,
  appSessionTimeElapsed: ?number,
  appVersion: string,
  deviceId: ?string,
  isDebugBuild: boolean,
  isInternalUser: boolean,
  platform: string,
  persistentId: ?string,
  ts: number,
  st: number,
  userSessionId: ?string,
  userSessionTimeElapsed: ?number,
|};

type DeviceDataType = {|
  androidDeviceId?: string,
  dataProvider?: string,
  isLowPowerModeEnabled?: boolean,
  manu?: string,
  model?: string,
  wifi?: boolean,
|};

type CommonDataType = {|
  ...DeviceDataType,
  ...MinimalDataType,
  age: ?number,
  advertisingId: string,
  authId: ?string,
  deviceType: string,
  gender: number,
  languageSetting: string,
  lat: ?number,
  lon: ?number,
  osVersion: string,
  targetGender: ?GenderFilter,
  uid: ?string,
|};

export function getMinimalData(
  state: GlobalStateType,
  perfNow: number = perfTimer()
): MinimalDataType {
  const { isDebugBuild, platform } = get(config, ['sparks']);
  const timestamp = getAppSessionAdjustedTime(state, perfNow);
  return {
    appBuild: BUILD_NUMBER,
    appSessionId: getAppSessionId(state),
    appSessionTimeElapsed: getAppSessionTimeElapsed(state, perfNow),
    appVersion: process.env.VERSION,
    deviceId,
    isDebugBuild,
    isInternalUser: isTinderEmail.test(getEmail(state)),
    persistentId: deviceId,
    platform,
    ts: timestamp,
    st: timestamp,
    userSessionId: getUserSessionId(state),
    userSessionTimeElapsed: getUserSessionTimeElapsed(state, perfNow),
  };
}

export function getCommonData(state: GlobalStateType, perfNow?: number): CommonDataType {
  const {
    android_device_id: androidDeviceId,
    dataprovider: dataProvider,
    is_low_power_mode_enabled: isLowPowerModeEnabled,
    manu,
    model,
    wifi,
  } = getQueryParams(state);

  // passed by query param (Tinder Lite)
  const deviceData: DeviceDataType = {
    androidDeviceId,
    dataProvider,
    isLowPowerModeEnabled,
    manu,
    model,
    wifi,
  };

  const birthday = getBirthday(state);
  return {
    ...deviceData,
    ...getMinimalData(state, perfNow),
    age: birthday != null ? calculateAge(birthday) : null,
    advertisingId: '', // include push token if we have it
    authId: get(state, ['sparks', 'authId']), // Temp id for users that don't complete auth process.
    deviceType: getLayout(state),
    gender: getGender(state),
    osVersion: navigator.userAgent,
    languageSetting: getCurrentLanguage(state),
    lat: getLatitude(state),
    lon: getLongitude(state),
    targetGender: getGenderFilter(state),
    uid: getProfileUserId(state) || getOnboardingUserId(state), // Use onboarding id before we have a profile
  };
}
